import { useEffect, useId, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement | string,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'flexible' | 'compact';
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

interface TurnstileWidgetProps {
  onToken: (token: string) => void;
  onError?: () => void;
  siteKey?: string;
}

const DEFAULT_SITE_KEY = '1x00000000000000000000AA'; // Cloudflare's always-pass test key

export function TurnstileWidget({ onToken, onError, siteKey }: TurnstileWidgetProps) {
  const containerId = useId();
  const widgetIdRef = useRef<string | undefined>(undefined);
  const mountedRef = useRef(false);

  useEffect(() => {
    // StrictMode would otherwise render the widget twice.
    if (mountedRef.current) return;
    mountedRef.current = true;

    const tryRender = () => {
      if (!window.turnstile) return false;
      const id = window.turnstile.render(`#${CSS.escape(containerId)}`, {
        sitekey: siteKey ?? (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? DEFAULT_SITE_KEY,
        callback: onToken,
        'error-callback': onError,
        theme: 'light',
        size: 'flexible',
      });
      widgetIdRef.current = id;
      return true;
    };

    if (!tryRender()) {
      const t = setInterval(() => {
        if (tryRender()) clearInterval(t);
      }, 200);
      return () => clearInterval(t);
    }
    return undefined;
  }, [containerId, onToken, onError, siteKey]);

  return <div id={containerId} className="min-h-[65px]" />;
}
